import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { iFullStoreState, iList, iTagInfoBase } from '../../shared/interfaces';
import { ALL_DEVICES_TAG } from '../../shared/helpers';
import { Set } from 'immutable';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import styles from './MapLegend.module.scss';

interface IMapLegendProps {
  tags: iList<iTagInfoBase<any>>;
  showedTagsIds: Set<string>;
}

const MapLegend: React.FC<IMapLegendProps> = ({ tags, showedTagsIds }) => {
  const history = useHistory();
  const isGeoFenceContainerVisible = useSelector<iFullStoreState, boolean>(
    (state) => state.fencesContainerUI.isGeoFenceContainerVisible
  );
  const isMapLegendVisible = useSelector<iFullStoreState, boolean>(
    (state) => state.mapLegendUI.isMapLegendVisible
  );

  const legendTags = useMemo(() => {
    return Object.values(tags)
      .filter((tag) => {
        const hasLegendFlag = tag.details.showInLegend === true;
        const isNotAllDevices = tag.details.id !== ALL_DEVICES_TAG;
        return hasLegendFlag && isNotAllDevices;
      })
      .sort((a, b) => {
        const nameA = a.details.name?.toLowerCase() || '';
        const nameB = b.details.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
  }, [tags, showedTagsIds]);

  // Hide legend if no tags or if geofence is visible or if legend is manually hidden
  if (legendTags.length === 0 || isGeoFenceContainerVisible || !isMapLegendVisible) {
    return null;
  }

  return (
    <div className={styles.legendContainer}>
      <div className={styles.legendContent}>
        <h3 className={styles.legendTitle}>Tags Legend</h3>
        <div className={styles.legendItems}>
          {legendTags.map((tag) => {
            const pinColor = tag.details.color ?? '#000';
            const hasColor = pinColor && pinColor !== '';
            const isViewed = showedTagsIds.has(tag.details.id);

            return (
              <button
                key={tag.details.id}
                type='button'
                className={classNames(styles.legendItem, { [styles.legendItem_active]: isViewed })}
                onClick={() => history.push(`/tags?viewTag=${encodeURIComponent(tag.details.id)}`)}
                title={isViewed ? 'Hide on map' : 'View on map'}
                aria-label={isViewed ? 'Hide on map' : 'View on map'}
              >
                <span className={styles.pinIcon} style={{ color: pinColor }}>
                  <svg viewBox="0 0 24 24" className={styles.pinSvg}>
                    <path
                      fill={hasColor ? 'currentColor' : 'none'}
                      stroke={hasColor ? 'none' : 'currentColor'}
                      strokeWidth={hasColor ? 0 : 1.5}
                      d="M12 2c-4.1 0-7.5 3.3-7.5 7.4 0 5.6 7.5 12.6 7.5 12.6S19.5 15 19.5 9.4C19.5 5.3 16.1 2 12 2z"
                    />
                  </svg>
                </span>
                <span className={styles.tagName}>{tag.details.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MapLegend;
